import { Injectable } from '@angular/core';
import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http';
import { throwError, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Http service to interact with backend api
 * Injectable - will be available in overall the application scope
 */
@Injectable({
  providedIn: 'root',
})
export class HttpService {
  // private http: HttpClient;

  /**
   * Creates an instance of http service.
   * @param http Http backend handler to by pass interceptors
   */
  constructor(private http: HttpClient) {
    // handler: HttpBackend
  }

  /**
   * Gets headers
   * @param token Auth token
   * @returns Http headers
   */
  private getHeaders(token: string): HttpHeaders {
    const headersConfig = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      // 'X-WP-Nonce': `${token}`,
      Authorization: `${token}`,
    };
    return new HttpHeaders(headersConfig);
  }

  /**
   * Formats errors
   * @param error Error received in api response
   * @returns Throw error after some processing
   */
  private handleErrors(error: any) {
    return throwError(error);
  }

  /**
   * Gets http service
   * @template T Generic type template
   * @param token Auth token
   * @param path Api end point
   * @param [params] Http parameters
   * @param headers Http headers
   * @returns Api response with status codes
   */
  get<T>(
    token: string,
    path: string,
    params: HttpParams = new HttpParams(),
    headers: HttpHeaders = this.getHeaders(token)
  ): Observable<T> {
    return this.http
      .get<T>(path, { headers: headers, params: params })
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Posts http service
   * @template T Generic type template
   * @param token Auth token
   * @param path Api end point
   * @param [body] Post data
   * @param headers Http headers
   * @returns api Response with status codes
   */
  post<T>(
    token: string,
    path: string,
    body: Object = {},
    headers: HttpHeaders = this.getHeaders(token)
  ): Observable<T> {
    const requestBody = body instanceof Blob ? body : JSON.stringify(body);
    return this.http
      .post<T>(path, requestBody, { headers: headers })
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Puts http service
   * @template T Generic type template
   * @param token Auth token
   * @param path Api end point
   * @param [body] Post data
   * @param headers Http headers
   * @returns Api response with status codes
   */
  put<T>(
    token: string,
    path: string,
    body: object = {},
    headers: HttpHeaders = this.getHeaders(token)
  ): Observable<T> {
    const requestBody = body instanceof Blob ? body : JSON.stringify(body);
    return this.http
      .put<T>(path, requestBody, { headers: headers })
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Patch http service
   * @template T Generic type template
   * @param token Auth token
   * @param path Api end point
   * @param [body] Post data
   * @param headers Http headers
   * @returns Api response with status codes
   */
  patch<T>(
    token: string,
    path: string,
    body: object = {},
    headers: HttpHeaders = this.getHeaders(token)
  ): Observable<T> {
    const requestBody = body instanceof Blob ? body : JSON.stringify(body);
    return this.http
      .patch<T>(path, requestBody, { headers: headers })
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Deletes http service
   * @template T Generic type template
   * @param token Auth token
   * @param path Api end point
   * @param headers Http headers
   * @returns Api response with status codes
   */
  delete<T>(
    token: string,
    path: string,
    headers: HttpHeaders = this.getHeaders(token)
  ): Observable<T> {
    return this.http
      .delete<T>(path, { headers: headers })
      .pipe(catchError(this.handleErrors));
  }
}
